<template>
    <Teleport to="body">
        <div
            v-if="isAvailable"
            ref="entryElement"
            data-testid="debug-entry-button-position"
            class="pointer-events-auto fixed bottom-4 right-4 z-[100002] select-none"
            :style="entryPositionStyle"
        >
            <ElTooltip content="开发调试" placement="left" :disabled="isDragging">
                <button
                    type="button"
                    data-testid="debug-entry-button"
                    class="relative flex h-11 w-11 touch-none cursor-grab items-center justify-center rounded-full border border-gray-30 bg-white text-gray-70 shadow-[0_4px_16px_rgba(0,0,0,0.18)] transition-colors hover:bg-gray-10 hover:text-gray-90 focus:outline-none focus:ring-2 focus:ring-eureka-500 focus:ring-offset-2 active:cursor-grabbing"
                    aria-label="打开开发调试"
                    @pointerdown="handlePointerDown"
                    @click="handleClick"
                >
                    <PtIcon icon="SolidSettings" class="text-xl fill-current" />
                    <span
                        v-if="hasBusinessMock"
                        data-testid="debug-business-mock-dot"
                        class="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-white bg-red-500"
                        aria-label="存在生效中的业务 Mock"
                    />
                </button>
            </ElTooltip>
        </div>
    </Teleport>

    <ElDialog
        v-if="isAvailable"
        v-model="dialogVisible"
        :appendToBody="true"
        title="开发调试"
        width="min(1000px, 90vw)"
        class="debug-entry-dialog"
        :zIndex="100003"
        :closeOnClickModal="true"
        :closeOnPressEscape="true"
    >
        <DebugContent />
    </ElDialog>
</template>

<script setup lang="ts">
import {
    computed,
    onBeforeUnmount,
    onMounted,
    ref,
} from 'vue';
import { ElDialog, ElTooltip } from 'element-plus';
import { PtIcon } from 'patsnap-biz';
import '@patsnap-ui/icon/assets/solid/settings.svg';
import DebugContent from './DebugContent.vue';
import { isDebugEntryEnvironment } from './debug-entry-environment';
import { useDebugSettings } from './use-debug-settings';

defineOptions({
    name: 'DebugEntryButton',
});

type ButtonPosition = {
    x: number;
    y: number;
};

const POSITION_STORAGE_KEY = 'debug-entry-button-position';
const DEFAULT_BUTTON_SIZE = 44;
const DRAG_THRESHOLD = 4;
const dialogVisible = ref(false);
const entryElement = ref<HTMLElement | null>(null);
const position = ref<ButtonPosition | null>(null);
const isDragging = ref(false);
const isAvailable = isDebugEntryEnvironment(
    import.meta.env.DEV,
    import.meta.env.VITE_DEPLOY_ENV,
);
const { hasBusinessMock } = useDebugSettings();
const entryPositionStyle = computed(() => position.value
    ? {
        bottom: 'auto',
        left: `${position.value.x}px`,
        right: 'auto',
        top: `${position.value.y}px`,
    }
    : undefined);

let dragOffset = { x: 0, y: 0 };
let dragStart = { x: 0, y: 0 };
let didDrag = false;
let suppressNextClick = false;
let suppressClickTimer: number | undefined;

function clampPosition(nextPosition: ButtonPosition): ButtonPosition {
    const rect = entryElement.value?.getBoundingClientRect();
    const width = rect?.width || DEFAULT_BUTTON_SIZE;
    const height = rect?.height || DEFAULT_BUTTON_SIZE;

    return {
        x: Math.min(Math.max(nextPosition.x, 0), Math.max(window.innerWidth - width, 0)),
        y: Math.min(Math.max(nextPosition.y, 0), Math.max(window.innerHeight - height, 0)),
    };
}

function persistPosition() {
    if (!position.value) return;

    localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(position.value));
}

function restorePosition() {
    const storedPosition = localStorage.getItem(POSITION_STORAGE_KEY);
    if (!storedPosition) return;

    try {
        const parsedPosition = JSON.parse(storedPosition) as Partial<ButtonPosition>;
        if (Number.isFinite(parsedPosition.x) && Number.isFinite(parsedPosition.y)) {
            position.value = clampPosition({
                x: parsedPosition.x as number,
                y: parsedPosition.y as number,
            });
        }
    } catch {
        localStorage.removeItem(POSITION_STORAGE_KEY);
    }
}

function removeDragListeners() {
    window.removeEventListener('pointermove', handlePointerMove);
    window.removeEventListener('pointerup', handlePointerEnd);
    window.removeEventListener('pointercancel', handlePointerEnd);
}

function handlePointerDown(event: PointerEvent) {
    if (event.button !== 0) return;

    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    dragOffset = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
    };
    dragStart = { x: event.clientX, y: event.clientY };
    didDrag = false;
    isDragging.value = true;
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerEnd);
    window.addEventListener('pointercancel', handlePointerEnd);
}

function handlePointerMove(event: PointerEvent) {
    if (!isDragging.value) return;

    if (Math.hypot(event.clientX - dragStart.x, event.clientY - dragStart.y) >= DRAG_THRESHOLD) {
        didDrag = true;
    }
    if (!didDrag) return;

    event.preventDefault();
    position.value = clampPosition({
        x: event.clientX - dragOffset.x,
        y: event.clientY - dragOffset.y,
    });
}

function handlePointerEnd() {
    if (!isDragging.value) return;

    if (didDrag) {
        persistPosition();
        suppressNextClick = true;
        window.clearTimeout(suppressClickTimer);
        suppressClickTimer = window.setTimeout(() => {
            suppressNextClick = false;
        });
    }
    isDragging.value = false;
    removeDragListeners();
}

function handleClick(event: MouseEvent) {
    if (suppressNextClick) {
        event.preventDefault();
        event.stopPropagation();
        suppressNextClick = false;
        return;
    }

    dialogVisible.value = true;
}

function handleViewportResize() {
    if (!position.value) return;

    position.value = clampPosition(position.value);
    persistPosition();
}

onMounted(() => {
    restorePosition();
    window.addEventListener('resize', handleViewportResize);
});

onBeforeUnmount(() => {
    removeDragListeners();
    window.removeEventListener('resize', handleViewportResize);
    window.clearTimeout(suppressClickTimer);
});
</script>

<style lang="scss">
.debug-entry-dialog.el-dialog {
    @apply flex max-h-[90vh] flex-col;
    margin: 5vh auto;

    .el-dialog__body {
        @apply min-h-0 overflow-y-auto pt-2;
    }
}
</style>
